/**
 * ============================================================================
 * METRICS SELECTORS — home-page card views over getMetrics + the catalog.
 * ============================================================================
 *
 * GENERIC LOGIC — no metric names are hardcoded here. Each metric's `type`
 * comes from the getMetricDefinitions catalog and decides the aggregation:
 *   percentage → 100 × SUM(numeratorValue) / SUM(denominatorValue)
 *   volume     → SUM(value), with attainment when the catalog sets baseMetricKey
 *   count      → SUM(value), no attainment (informational)
 *
 * Home page renders:
 *   Top section:    all 'percentage' metrics
 *   Bottom section: all 'volume' + 'count' metrics
 */

import {
  DcrRecord,
  GetMetricsOutput,
  MetricDefinition,
  PeriodTimeline,
} from 'src/types/metricsApi.types';
import {
  MetricGroupSelection,
  MetricNameSelection,
  metricGroupMatches,
  metricNameMatches,
} from 'src/features/api/filterPredicate';
import {
  OTD_CARRIER_KEYS,
  TABLE_ONLY_METRIC_KEYS,
} from 'src/features/api/tableSelectors';
import {
  MetricCatalog,
  accumulateMetric,
  buildMetricCatalog,
  finalizeRate,
  metricUnit,
  normalizeMetricType,
  parseReportingPeriod,
} from 'src/features/api/aggregationCore';

// ──── Output types ────

export interface PercentageMetricData {
  metricName: string;
  owner: string;
  goal: number;
  /** null = the metric had no observations that week (NOT the same as 0%). */
  weeklyValues: Array<number | null>;
  weeks: string[];
}

export interface VolumeMetricData {
  metricName: string;
  owner: string;
  goal: number;
  /** "GTE" (higher better, default) | "LTE" (lower better) — goal comparator. */
  goalDirection: string;
  /** Display unit for the goal (metricUnit — catalog unit or type fallback). */
  unit: string;
  /** Empty string = no baseline (render as raw count like Demand). */
  baselineMetricName: string;
  /** null = no observations that week (NOT the same as a real 0). */
  weeklyVolumes: Array<number | null>;
  /** Empty array = no attainment (no baseline); null = no data that week. */
  weeklyAttainment: Array<number | null>;
  weeks: string[];
}

export interface WbrDashboardData {
  weeks: string[];
  /**
   * Timeline per week, aligned with `weeks`. FUTURE weeks carry
   * forecast/commitment — they render in the trends but are never the
   * headline-value fallback.
   */
  weekTimelines: PeriodTimeline[];
  percentageMetrics: PercentageMetricData[];
  volumeMetrics: VolumeMetricData[];
}

// ──── Main selector ────

/**
 * True when the catalog declares this preset on any definition.
 *
 * Guard, not decoration: `presets` is optional on the wire. If the deployed
 * catalog carries none, filtering by preset would empty the page. Better to
 * show every present metric than nothing at all, so the filter turns itself off
 * when no definition mentions the preset.
 */
export function catalogHasPreset(
  definitions: MetricDefinition[] | undefined,
  preset: string,
): boolean {
  return (definitions ?? []).some((definition) =>
    definition.presets?.includes(preset),
  );
}

export function selectWbrDashboardData(
  response: GetMetricsOutput | undefined,
  definitions: MetricDefinition[] | undefined,
  dcrPredicate?: (dcr: DcrRecord) => boolean,
  /** Metric Group chips — scope the volume cards only (see below). */
  metricGroups?: MetricGroupSelection,
  /**
   * Preset SCOPE — not a filter. The home page passes WBR_METRICS_ONLY_PRESET
   * because that page IS the WBR metric set; it is never exposed as a control
   * and no chip can widen it. (Metric Group, above, is the filter.)
   */
  preset?: string,
  /** Metric Name chips — explicit metric picks, applies to every card type. */
  metricNames?: MetricNameSelection,
): WbrDashboardData | null {
  if (!response || response.data.length === 0 || !definitions?.length)
    return null;

  // FUTURE periods are INCLUDED (spec domains-page-week-picker R3.3,
  // superseding default-metrics-window R4.1): the trend shows forecast weeks
  // after the selected reporting week, exactly like the tables' Forward
  // group. weekTimelines lets the page keep them out of headline fallbacks.
  // BUT an all-FUTURE response has no reportable week — every headline would
  // fall back to a forecast, which "a forecast never masquerades as the
  // headline actual" forbids. Treat it as no data (the empty state), same as
  // before forecasts were included.
  if (response.data.every((period) => period.timeline === 'FUTURE'))
    return null;

  // Row-level filters (QuickSight parity): constrain DCRs before aggregation.
  const periods = dcrPredicate
    ? response.data.map((period) => ({
        ...period,
        dcrData: period.dcrData.filter(dcrPredicate),
      }))
    : response.data;
  const weeks = periods.map(
    (period) => parseReportingPeriod(period.reportingPeriod).label,
  );
  const weekTimelines = periods.map((period) => period.timeline);

  const catalog: MetricCatalog = buildMetricCatalog(definitions);

  // Only metrics that actually appear in NON-forecast data become cards. A
  // metric present solely in forecast weeks would otherwise mint a card whose
  // whole historical/current series is null (headline blank, trend just the
  // forecast). FUTURE weeks still contribute VALUES (below) to a card that
  // already exists — they just don't create one.
  const present = new Set<string>();
  for (const period of periods) {
    if (period.timeline === 'FUTURE') continue;
    for (const dcr of period.dcrData) {
      for (const entry of dcr.metrics) present.add(entry.metricKey);
    }
  }
  // Preset scope DEGRADES SAFELY: apply it only when the catalog actually
  // declares the preset on some definition. `presets` is optional on the wire,
  // so if the deployed catalog ships none, filtering strictly would empty the
  // home page even though metrics data is present — a hard regression. When no
  // definition carries the preset we show every present metric instead (matches
  // catalogHasPreset's documented "turns itself off" behaviour).
  const presetActive = preset ? catalogHasPreset(definitions, preset) : false;

  // Metric Group and Metric Name filter EVERY card type — percentage cards
  // included. (The legacy dashboard's "OTD ignores the group control" was a
  // QuickSight limitation, deliberately not copied.) The "WBR Metrics Only"
  // chip matches by preset membership (metricGroupMatches).
  const ordered = definitions
    .filter((definition) => present.has(definition.metricKey))
    // Per-DCR OTD rates feed the derived %DCRs rows on the detail page; they
    // are never their own card and must never be averaged.
    .filter((definition) => !OTD_CARRIER_KEYS.has(definition.metricKey))
    // Table-only metrics (L1 quality trio) never card on the home page —
    // their home is the DCR Health table section, in either mode.
    .filter((definition) => !TABLE_ONLY_METRIC_KEYS.has(definition.metricKey))
    .filter(
      (definition) =>
        !presetActive || (definition.presets ?? []).indexOf(preset!) !== -1,
    )
    .filter((definition) => metricGroupMatches(definition, metricGroups))
    .filter((definition) => metricNameMatches(definition.name, metricNames))
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const percentageMetrics: PercentageMetricData[] = [];
  const volumeMetrics: VolumeMetricData[] = [];

  for (const definition of ordered) {
    const owner = definition.owner ?? '';
    const goal = parseFloat(definition.goal ?? '0') || 0;

    if (normalizeMetricType(definition.type) === 'percentage') {
      const weeklyValues = periods.map((period) => {
        // FUTURE weeks carry forecast/commitment (volume), never a realized
        // RATE: a future week has a committed denominator but nothing
        // delivered yet, so finalizeRate would report a meaningless 0%. A rate
        // is what happened — exclude forecast weeks from the trend (volume
        // metrics below keep them). Fixes OTD plotting 0% for future weeks.
        if (period.timeline === 'FUTURE') return null;
        const { sumNum, sumDen, seen } = accumulateMetric(
          period.dcrData,
          definition.metricKey,
          catalog,
        );
        // No carrier in this week's slice → no data. Reporting 0% would read as
        // "we delivered nothing", which is a different claim.
        return seen ? finalizeRate(sumNum, sumDen) : null;
      });
      percentageMetrics.push({
        metricName: definition.name,
        owner,
        goal,
        weeklyValues,
        weeks,
      });
      continue;
    }

    // volume or count
    const weeklyVolumes = periods.map((period) => {
      const { sum, seen } = accumulateMetric(
        period.dcrData,
        definition.metricKey,
        catalog,
      );
      return seen ? sum : null;
    });
    const baseline = definition.baseMetricKey
      ? catalog.get(definition.baseMetricKey)
      : undefined;
    let weeklyAttainment: Array<number | null> = [];
    if (baseline) {
      const weeklyBaseline = periods.map((period) => {
        const { sum, seen } = accumulateMetric(
          period.dcrData,
          baseline.metricKey,
          catalog,
        );
        return seen ? sum : null;
      });
      weeklyAttainment = weeklyVolumes.map((volume, i) => {
        const base = weeklyBaseline[i];
        return volume === null || base === null
          ? null
          : finalizeRate(volume, base);
      });
    }

    volumeMetrics.push({
      metricName: definition.name,
      owner,
      goal,
      goalDirection: (definition.goalDirection ?? 'GTE').toUpperCase(),
      unit: metricUnit(definition),
      baselineMetricName: baseline?.name ?? '',
      weeklyVolumes,
      weeklyAttainment,
      weeks,
    });
  }

  return { weeks, weekTimelines, percentageMetrics, volumeMetrics };
}
